import { useCallback, useEffect, useState } from 'react';
import { healthProfileService } from '@/services/health-profile.service';
import { MedicalDetailType } from '@/types/health-profile';
import familyStatsService from '@/services/familyStats.service';

// Generic profile field hook for any profile field (e.g., allergies, bloodType, etc)
export function useProfileField<T = any>(
  memberId: string,
  type: MedicalDetailType,
  defaultValue: T
) {
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [fieldValue, setFieldValue] = useState<T>(defaultValue);
  const [originalValue, setOriginalValue] = useState<T>(defaultValue);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await healthProfileService.getProfileField(memberId, type);
      setProfile(result);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    if (profile && type in profile) {
      setFieldValue(profile[type] ?? defaultValue);
      setOriginalValue(profile[type] ?? defaultValue);
    }
  }, [profile, type, defaultValue]);

  const saveField = async (value: T) => {
    const updatedProfile = { [type]: value };
    const result = await healthProfileService.createOrUpdateField(
      memberId,
      type,
      updatedProfile
    );
    setProfile(result);
    setFieldValue(value);
    await fetchProfile();
    
    // Refetch family stats to update profile score/strength
    try {
      await familyStatsService.getFamilyStats();
    } catch (err) {
      console.error('Failed to refresh family stats:', err);
    }
  };

  // Save without refetch/loading (for specific use-cases like MRadio in BirthComplications)
  const saveFieldNoRefetch = async (value: T) => {
    const updatedProfile = { ...profile, [type]: value };
    const result = await healthProfileService.createOrUpdateField(
      memberId,
      type,
      updatedProfile
    );
    setProfile(result);
    setFieldValue(value);
    // No fetchProfile or loading state
    
    // Still refetch family stats to update profile score/strength
    try {
      await familyStatsService.getFamilyStats();
    } catch (err) {
      console.error('Failed to refresh family stats:', err);
    }
  };

  const isChanged = () => {
    if (fieldValue === originalValue) return false;

    if (typeof fieldValue === "string" && typeof originalValue === "string") {
      return fieldValue !== originalValue;
    }

    // If both are objects/arrays, do a deep comparison
    if (
      typeof fieldValue === "object" &&
      typeof originalValue === "object" &&
      fieldValue !== null &&
      originalValue !== null
    ) {
      if (Array.isArray(fieldValue) && Array.isArray(originalValue)) {
        if (fieldValue.length !== originalValue.length) return true;

        for (let i = 0; i < fieldValue.length; i++) {
          if (
            typeof fieldValue[i] === "object" &&
            typeof originalValue[i] === "object"
          ) {
            if (
              JSON.stringify(fieldValue[i]) !== JSON.stringify(originalValue[i])
            ) {
              return true;
            }
          } else if (fieldValue[i] !== originalValue[i]) {
            return true;
          }
        }
        return false;
      }

      // For objects, compare stringified versions
      return JSON.stringify(fieldValue) !== JSON.stringify(originalValue);
    }
  };

  return {
    profile,
    fieldValue,
    setFieldValue,
    isChanged,
    saveField,
    saveFieldNoRefetch,
    loading,
    error,
    refetch: fetchProfile,
  };
}
